import React, { useState } from 'react';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Card from '../../ui/Card';
import { getTournamentTierOptionsSync, getSimpleTierLabel } from '../../../utils/tierUtils';
import { syncTournamentPlayersFromSlashGolf } from '../adminApiService';
import toast from 'react-hot-toast';

const TournamentPlayersTab = ({
  tournament,
  tournamentPlayers,
  availableGolfers,
  canModify,
  playersLoading,
  golfersLoading,
  addPlayerMutation,
  removePlayerMutation,
  updateTierMutation,
  queryClient
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGolfer, setSelectedGolfer] = useState(null);
  const [selectedTier, setSelectedTier] = useState('TIER_D');
  const [activeView, setActiveView] = useState('assigned'); // assigned, available
  const [isSyncingPlayers, setIsSyncingPlayers] = useState(false);

  // Get unassigned golfers
  const unassignedGolfers = (availableGolfers || []).filter(golfer => 
    !(tournamentPlayers || []).find(tp => tp.golferId === golfer.id)
  );

  // Filter players based on search
  const filteredTournamentPlayers = (tournamentPlayers || []).filter(player =>
    player.golfer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUnassignedGolfers = unassignedGolfers.filter(golfer =>
    golfer.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddPlayer = () => {
    if (selectedGolfer) {
      addPlayerMutation.mutate({ golferId: selectedGolfer.id, tier: selectedTier });
      setSelectedGolfer(null);
      setSelectedTier('TIER_D');
    }
  };

  const handleRemovePlayer = (golferId) => {
    if (window.confirm('Are you sure you want to remove this player from the tournament?')) {
      removePlayerMutation.mutate(golferId);
    }
  };

  const handleTierChange = (golferId, newTier) => {
    updateTierMutation.mutate({ golferId, tier: newTier });
  };

  const handleSyncPlayers = async () => {
    if (!tournament?.tournId) {
      toast.error('This tournament was not imported from SlashGolf API and cannot be synced');
      return;
    }

    if (!window.confirm('Sync players from SlashGolf API? This will add any missing players to the tournament.')) {
      return;
    }

    setIsSyncingPlayers(true);
    const loadingToast = toast.loading('Syncing players from SlashGolf...');
    
    try {
      const result = await syncTournamentPlayersFromSlashGolf(tournament.id);
      
      toast.dismiss(loadingToast);
      
      if (result.added > 0 || result.removed > 0) {
        const message = [
          `✅ Sync complete!`,
          result.added > 0 ? `• ${result.added} new player${result.added !== 1 ? 's' : ''} added` : null,
          `• ${result.existing} already existed`,
          result.removed > 0 ? `• ${result.removed} player${result.removed !== 1 ? 's' : ''} removed` : null,
          result.refunded > 0 ? `• ${result.refunded} refund${result.refunded !== 1 ? 's' : ''} processed ($${result.refundedAmount?.toFixed(2) || '0.00'})` : null,
          result.leaderboardUpdated ? '• Leaderboard updated' : null,
          result.maxPlayersUpdated ? `• Max players updated to ${result.maxPlayersUpdated}` : null
        ].filter(Boolean).join('\n');
        
        const toastType = result.refunded > 0 ? 'warning' : 'success';
        toast[toastType](message, { duration: 6000 });
        
        // Invalidate queries to refetch tournament players and tournament data
        if (queryClient) {
          queryClient.invalidateQueries({ queryKey: ['tournament-players', tournament.id] });
          queryClient.invalidateQueries({ queryKey: ['admin-tournaments'] });
          queryClient.invalidateQueries({ queryKey: ['golfers'] });
        }
      } else if (result.existing > 0) {
        const message = [
          `All ${result.existing} players are already in the tournament`,
          result.maxPlayersUpdated ? `Max players set to ${result.maxPlayersUpdated}` : null
        ].filter(Boolean).join('\n');
        
        toast.success(message);
        
        // Still refresh to show any updates
        if (queryClient) {
          queryClient.invalidateQueries({ queryKey: ['admin-tournaments'] });
        }
      } else {
        toast.info('No players found in SlashGolf API for this tournament');
      }

      if (result.errors && result.errors.length > 0) {
        console.error('🚨 SYNC ERRORS DETECTED:');
        console.error('   Tournament ID:', tournament.id);
        console.error('   Tournament Name:', tournament.name);
        console.error('   Errors:', result.errors);
        console.error('   Full Result:', result);
        
        // Log each error individually for better visibility
        result.errors.forEach((error, index) => {
          console.error(`   Error ${index + 1}:`, error);
        });
        
        toast.error(`${result.errors.length} error(s) occurred during sync. Check console for details.`);
      }

    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Sync error:', error);
      toast.error(`Failed to sync players: ${error.message}`);
    } finally {
      setIsSyncingPlayers(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'TIER_A': return '#ff6b6b'; // Red
      case 'TIER_B': return '#ffa500'; // Orange  
      case 'TIER_C': return '#4dabf7'; // Blue
      case 'TIER_D': return '#51cf66'; // Green
      default: return '#868e96'; // Gray
    }
  };

  const getTierPrice = (tier) => {
    switch (tier) {
      case 'TIER_A': return '$250';
      case 'TIER_B': return '$200';
      case 'TIER_C': return '$150';
      case 'TIER_D': return '$100';
      default: return '$100';
    }
  };

  return (
    <div className="admin-tournament-players-tab">
      <div className="tab-header">
        <div>
          <h2>Tournament Players</h2>
          <p>Manage golfers assigned to this tournament</p>
        </div>
        {tournament?.tournId && (
          <Button
            onClick={handleSyncPlayers}
            disabled={isSyncingPlayers || !canModify}
            variant="secondary"
            className="sync-players-btn"
            style={{ marginLeft: 'auto' }}
          >
            {isSyncingPlayers ? '🔄 Syncing...' : '🏌️ Sync from SlashGolf'}
          </Button>
        )}
      </div>

      <div className="players-controls">
        <div className="view-toggle">
          <button
            className={`toggle-btn ${activeView === 'assigned' ? 'active' : ''}`}
            onClick={() => setActiveView('assigned')}
          >
            Assigned Players <span className="toggle-count">{tournamentPlayers?.length || 0}</span>
          </button>
          <button
            className={`toggle-btn ${activeView === 'available' ? 'active' : ''}`}
            onClick={() => setActiveView('available')}
            disabled={!canModify}
          >
            Add Players <span className="toggle-count">{unassignedGolfers.length}</span>
          </button>
        </div>

        <div className="search-controls">
          <Input
            type="text"
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            lightTheme={true}
          />
        </div>
      </div>

      <div className="players-content">
        {activeView === 'assigned' && (
          <div className="assigned-players">
            {!canModify && (
              <div className="modification-notice">
                ⚠️ Player modifications are restricted - tournament is not in DRAFT status
              </div>
            )}

            {playersLoading ? (
              <div className="loading-state">Loading players...</div>
            ) : filteredTournamentPlayers.length === 0 ? (
              <Card className="empty-state">
                <h3>No Players Assigned</h3>
                <p>This tournament has no assigned players yet.</p>
                {canModify && (
                  <Button onClick={() => setActiveView('available')}>
                    Add Players
                  </Button>
                )}
              </Card>
            ) : (
              <div className="players-grid">
                {filteredTournamentPlayers.map((player) => (
                  <Card key={player.id} className="player-card">
                    <div className="player-header">
                      <div className="player-info">
                        <h4>{player.golfer?.name}</h4>
                        <div className="player-details">
                          <span>Ranking: #{player.golfer?.worldRanking}</span>
                          <span>Country: {player.golfer?.country}</span>
                        </div>
                      </div>
                      <div className="player-status">
                        {player.isAvailable ? (
                          <span className="status-badge available">Available</span>
                        ) : (
                          <span className="status-badge owned">Owned</span>
                        )}
                      </div>
                    </div>

                    <div className="player-tier">
                      <label>Tier:</label>
                      <select
                        value={player.tier}
                        onChange={(e) => handleTierChange(player.golferId, e.target.value)}
                        disabled={!canModify}
                        style={{ 
                          backgroundColor: getTierColor(player.tier),
                          color: 'white'
                        }}
                        className="tier-select"
                      >
                        {getTournamentTierOptionsSync().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label} ({getTierPrice(option.value)})
                          </option>
                        ))}
                      </select>
                    </div>

                    {canModify && (
                      <div className="player-actions">
                        <Button
                          onClick={() => handleRemovePlayer(player.golferId)}
                          className="remove-btn"
                          size="small"
                          disabled={removePlayerMutation.isLoading}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeView === 'available' && canModify && (
          <div className="available-players">
            {selectedGolfer && (
              <Card className="selected-golfer-panel">
                <h3>Adding: {selectedGolfer.name}</h3>
                <div className="tier-selection">
                  <label>Select Tier:</label>
                  <select
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value)}
                    style={{ backgroundColor: getTierColor(selectedTier), color: 'white' }}
                    className="tier-select"
                  >
                    {getTournamentTierOptionsSync().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label} ({getTierPrice(option.value)})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="add-actions">
                  <Button
                    onClick={handleAddPlayer}
                    disabled={addPlayerMutation.isLoading}
                    className="add-btn"
                  >
                    {addPlayerMutation.isLoading ? 'Adding...' : 'Add Player'}
                  </Button>
                  <Button
                    onClick={() => setSelectedGolfer(null)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            )}

            {golfersLoading ? (
              <div className="loading-state">Loading available golfers...</div>
            ) : filteredUnassignedGolfers.length === 0 ? (
              <Card className="empty-state">
                <h3>No Available Players</h3>
                <p>
                  {searchTerm 
                    ? 'No golfers match your search criteria.' 
                    : 'All golfers are already assigned to this tournament.'}
                </p>
              </Card>
            ) : (
              <div className="golfers-grid">
                {filteredUnassignedGolfers.map((golfer) => (
                  <Card 
                    key={golfer.id} 
                    className={`golfer-card ${selectedGolfer?.id === golfer.id ? 'selected' : ''}`}
                    onClick={() => setSelectedGolfer(golfer)}
                  >
                    <div className="golfer-info">
                      <h4>{golfer.name}</h4>
                      <div className="golfer-details">
                        <span>Ranking: #{golfer.worldRanking}</span>
                        <span>Country: {golfer.country}</span>
                      </div>
                    </div>
                    <div className="golfer-tier">
                      Default: Tier {golfer.tier}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentPlayersTab;